/* ege-calculator-app.js — page application for /ege-calculator.
 *
 * ESM module, no engine dependency: pure data → arithmetic. Owns all UI:
 * subject picker → (quick converter | detailed mock-exam scoring with
 * FIPI part-2 criteria) → result (test score + thresholds + goal gap +
 * loss breakdown + PDF + local history) with pause/resume via localStorage.
 *
 * Reads window.__EGE = { T: strings, cfg: { base, year, locale } }.
 * Subject data: {base}/{year}/manifest.json + per-subject JSON. All exam
 * content (task topics, criteria) is intentionally Russian-only — official
 * exam terminology; UI chrome is localized.
 *
 * Scoring rules implemented (russian essay, FIPI 2026):
 *   - words < minWords  → every criterion forced to 0
 *   - notSourceText flag → every criterion forced to 0
 *   - К1 = 0            → К2, К3 forced to 0 (cascade from subject JSON)
 */

const EGE = window.__EGE || { T: {}, cfg: {} };
const T = EGE.T;
const cfg = EGE.cfg;
const $ = (id) => document.getElementById(id);
const root = $('egcPanel');

const LS_SESSION = 'egc_session_v1';
const LS_HISTORY = 'egc_history_v1';
const SESSION_TTL_MS = 7 * 24 * 3600 * 1000; // mock reviews span days, not hours
const HISTORY_MAX = 60;
const SLUG = 'ege-calculator';

let manifest = null;
let subject = null;          // loaded subject JSON
const subjCache = new Map();
let firedStart = false;
let completedFired = false;
let abandonFired = false;
let currentStage = 'picker'; // for tool_abandon payload

// state of the current review
let st = null;

function blankState(subjectId) {
    return {
        subjectId,
        tab: 'quick',
        quickPrimary: 0,
        marks: {},        // taskN -> earned points (part 1); absent = not marked yet
        critMarks: {},    // `${taskN}:${code}` -> earned points (part 2)
        essayWords: null, // word count for essay-kind tasks
        flags: {},        // zero-flag id -> bool
        goal: null,
    };
}

function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function tr(key, repl) {
    let s = T[key] != null ? String(T[key]) : key;
    if (repl) for (const k in repl) s = s.split(':' + k).join(String(repl[k]));
    return s;
}
function announce(msg) { try { window.__announce && window.__announce(String(msg || '')); } catch (e) {} }
function fe(kind, extra) { try { window.__aeFeature && window.__aeFeature(kind, extra || {}); } catch (e) {} }
function ae(type, data) { try { window.__ae && window.__ae(type, Object.assign({ tool_slug: SLUG }, data || {})); } catch (e) {} }
function toast(msg, type) { try { window.__toast && window.__toast(msg, type || 'info'); } catch (e) {} }
function fireStart() { if (!firedStart) { firedStart = true; ae('tool_use', {}); } }
function stage(name, extra) { currentStage = name; ae('tool_stage', Object.assign({ stage: name }, extra || {})); }
// Reveal the tool-UI top on a view TRANSITION (not on in-place re-renders).
// Uses the #egcTop sentinel + block:'nearest' — only scrolls when it is off
// screen, never a jarring jump (global no-auto-scroll rule). Needed because
// swapping detail→result in place leaves the page scrolled at the footer.
function scrollTop() {
    try { const a = $('egcTop'); a && a.scrollIntoView({ block: 'nearest' }); } catch (e) {}
}

// ── data ──────────────────────────────────────────────────────────────────

async function loadManifest() {
    const r = await fetch(cfg.base + '/' + cfg.year + '/manifest.json');
    if (!r.ok) throw new Error('manifest ' + r.status);
    return r.json();
}
async function loadSubject(id) {
    if (subjCache.has(id)) return subjCache.get(id);
    const meta = manifest.subjects.find(s => s.id === id);
    if (!meta) throw new Error('unknown subject ' + id);
    const r = await fetch(cfg.base + '/' + cfg.year + '/' + meta.file);
    if (!r.ok) throw new Error('subject ' + r.status);
    const data = await r.json();
    subjCache.set(id, data);
    return data;
}

// ── localStorage ──────────────────────────────────────────────────────────

function savedSession() {
    try {
        const raw = localStorage.getItem(LS_SESSION);
        if (!raw) return null;
        const d = JSON.parse(raw);
        if (!d || !d.st || !d.st.subjectId || (Date.now() - (d.savedAt || 0)) > SESSION_TTL_MS) return null;
        return d;
    } catch (e) { return null; }
}
function saveSession() {
    try { localStorage.setItem(LS_SESSION, JSON.stringify({ st, savedAt: Date.now() })); } catch (e) {}
}
function clearSession() { try { localStorage.removeItem(LS_SESSION); } catch (e) {} }

function history() {
    try { return JSON.parse(localStorage.getItem(LS_HISTORY) || '[]') || []; } catch (e) { return []; }
}
function pushHistory(entry) {
    try {
        const h = history();
        h.push(entry);
        localStorage.setItem(LS_HISTORY, JSON.stringify(h.slice(-HISTORY_MAX)));
    } catch (e) {}
}

// ── scoring core ──────────────────────────────────────────────────────────

// Returns {zeroAll, cascadeZero:Set<code>} for an essay task given state.
function essayGates(task) {
    const gates = { zeroAll: false, cascadeZero: new Set() };
    if (!task || task.kind !== 'essay') return gates;
    const wr = task.wordRules;
    if (wr && wr.zeroUnderMin && st.essayWords != null && st.essayWords >= 0 && st.essayWords < wr.minWords) gates.zeroAll = true;
    for (const f of (task.zeroFlags || [])) if (st.flags[f.id]) gates.zeroAll = true;
    if (task.cascade) {
        for (const src in task.cascade) {
            const v = st.critMarks[task.n + ':' + src];
            if (v === 0) for (const dep of task.cascade[src]) gates.cascadeZero.add(dep);
        }
    }
    return gates;
}

// which source criterion (e.g. К1) zeroed this dependent one, for the note
function cascadeSourceOf(task, code) {
    if (!task.cascade) return '';
    for (const src in task.cascade) if (task.cascade[src].indexOf(code) !== -1) return src;
    return '';
}

function critEffective(task, crit) {
    const gates = essayGates(task);
    if (gates.zeroAll) return 0;
    if (gates.cascadeZero.has(crit.code)) return 0;
    const v = st.critMarks[task.n + ':' + crit.code];
    return typeof v === 'number' ? v : 0;
}

function detailPrimary() {
    let sum = 0;
    for (const t of subject.part1) {
        const v = st.marks[t.n];
        if (typeof v === 'number' && v > 0) sum += Math.min(v, t.max);
    }
    for (const t of subject.part2) for (const c of t.criteria) sum += critEffective(t, c);
    return sum;
}

function toTest(primary) {
    const p = Math.max(0, Math.min(primary, subject.maxPrimary));
    return subject.kind === 'grade' ? null : subject.scale[p];
}
function toGrade(primary) {
    if (subject.kind !== 'grade') return null;
    for (const g in subject.grades) {
        const [lo, hi] = subject.grades[g];
        if (primary >= lo && primary <= hi) return g;
    }
    return null;
}
// min primary that reaches a given test score; null if unreachable
function primaryForTest(goal) {
    if (subject.kind === 'grade') return null;
    for (let p = 0; p <= subject.maxPrimary; p++) if (subject.scale[p] >= goal) return p;
    return null;
}

// ── rendering ─────────────────────────────────────────────────────────────

function render(html) { root.innerHTML = html; }

function trustStrip() {
    return `<div class="egc-badge-line">
        <span class="egc-year-badge">${esc(tr('data_badge', { year: cfg.year }))}</span>
    </div>`;
}
function disclaimerCard() {
    return `<div class="egc-disclaimer">${esc(tr('disclaimer', { year: cfg.year }))}</div>`;
}

function fmtDuration(min) {
    const h = Math.floor(min / 60), m = min % 60;
    return m ? tr('duration_h_m', { h, m }) : tr('duration_h', { h });
}

// ── view: subject picker ──────────────────────────────────────────────────

// The picker is SERVER-rendered (#egcStart in the blade, from the same
// manifest.json) so it is on screen at first paint: this module script is
// deferred and wakes only after the whole layout has parsed (7.8 s on 4G + a mid
// phone on a sibling test, measured 2026-10-08). The node is kept and re-shown;
// what only this browser knows (an unfinished review, the exam-set sum, history)
// goes into its slots. A tap made before boot is handed over by
// window.__earlyAction (layout head) — see claimEarly().
const startNode = $('egcStart');
let startWired = false;

function viewPicker() {
    root.removeAttribute('data-boot');
    // A page served from a cache older than the blade has no #egcStart — say so
    // honestly instead of keeping a second copy of the picker markup here.
    if (!startNode) { render(`<div class="egc-card egc-center">${esc(T.load_error)}</div>`); return; }
    if (root.firstElementChild !== startNode || root.children.length !== 1) {
        root.textContent = '';
        root.appendChild(startNode);
    }
    if (!startWired) {
        startWired = true;
        startNode.querySelectorAll('.egc-subj').forEach(b => b.addEventListener('click', () => openSubject(b.dataset.subj)));
    }
    const saved = savedSession();
    let resumeHtml = '';
    if (saved) {
        const meta = manifest.subjects.find(s => s.id === saved.st.subjectId);
        if (meta) {
            resumeHtml = `<div class="egc-card egc-resume">
                <div class="egc-resume-t">${esc(tr('resume_title', { subject: meta.label }))}</div>
                <div class="egc-resume-d">${esc(T.resume_desc)}</div>
                <div class="egc-row">
                    <button type="button" class="egc-btn" id="egcResume">${esc(T.btn_resume)}</button>
                    <button type="button" class="egc-btn ghost" id="egcStartOver">${esc(T.btn_start_over)}</button>
                </div>
            </div>`;
        }
    }
    $('egcStartHead').innerHTML = resumeHtml + examSetCard();
    $('egcStartTail').innerHTML = historyCard();
    if (resumeHtml) {
        $('egcResume').addEventListener('click', async () => {
            fe('resume');
            st = saved.st;
            fireStart();
            try { subject = await loadSubject(st.subjectId); viewSubject(); }
            catch (e) { ae('tool_error', { error_type: 'resume_load_failed', s: st.subjectId }); clearSession(); toast(T.load_error, 'error'); viewPicker(); }
        });
        $('egcStartOver').addEventListener('click', () => { clearSession(); viewPicker(); });
    }
    bindExamSet();
    bindHistoryCard();
}

/** Take the subject buttons over from window.__earlyAction. `replay` — only
    when nothing is waiting to be resumed: a subject tapped before boot would
    start a new review over the unfinished one, whose card the person never saw. */
function claimEarly(replay) {
    if (!window.__earlyAction) return;
    window.__earlyAction.claim(SLUG, replay ? (id) => { openSubject(id); } : null);
}

// ── exam-set sum: best test score per saved subject → total vs university cutoff.
// Universities admit by the SUM of EGE scores across subjects — the real decision
// number, which no competitor calculator surfaces. Test-kind subjects only
// (matbase is a school grade, not counted toward admission).
const LS_TARGET = 'egc_target_sum';
function examSetBest() {
    const best = {};
    for (const e of history()) {
        const meta = manifest.subjects.find(s => s.id === e.s);
        if (!meta || meta.kind === 'grade' || e.t == null) continue;
        if (best[e.s] == null || e.t > best[e.s]) best[e.s] = e.t;
    }
    return best;
}
function examSetCard() {
    const best = examSetBest();
    const ids = Object.keys(best);
    if (!ids.length) return '';
    const total = ids.reduce((a, id) => a + best[id], 0);
    const chips = ids.map(id => {
        const meta = manifest.subjects.find(s => s.id === id) || { label: id };
        return `<span class="egc-set-chip">${esc(meta.label)} <b>${best[id]}</b></span>`;
    }).join('');
    let target = 0;
    try { target = parseInt(localStorage.getItem(LS_TARGET), 10) || 0; } catch (e) {}
    return `<div class="egc-card egc-set">
        <div class="egc-block-t">${esc(T.set_title)}</div>
        <div class="egc-set-chips">${chips}</div>
        <div class="egc-set-total">Σ <b>${total}</b></div>
        <div class="egc-set-sub">${esc(T.set_subtitle)}</div>
        <div class="egc-goal-row" style="margin-top:12px">
            <label class="egc-q-label" for="egcTarget">${esc(T.set_target_label)}</label>
            <input id="egcTarget" class="egc-goal-input" type="number" inputmode="numeric" min="0" max="900" value="${target || ''}" placeholder="240">
        </div>
        <div id="egcSetGap" class="egc-goal-out" data-total="${total}"></div>
        <div class="egc-goal-hint">${esc(T.set_hint)}</div>
    </div>`;
}
function bindExamSet() {
    const inp = $('egcTarget');
    if (!inp) return;
    const out = $('egcSetGap');
    const total = parseInt(out.dataset.total, 10) || 0;
    const show = () => {
        const target = parseInt(inp.value, 10);
        if (!target || target < 1) { out.textContent = ''; out.classList.remove('good'); try { localStorage.removeItem(LS_TARGET); } catch (e) {} return; }
        try { localStorage.setItem(LS_TARGET, String(target)); } catch (e) {}
        if (total >= target) { out.textContent = tr('set_reached', { target }); out.classList.add('good'); }
        else { out.textContent = tr('set_gap', { target, gap: target - total }); out.classList.remove('good'); }
        fe('target_sum', { t: target });
    };
    inp.addEventListener('input', show);
    show();
    fe('exam_set_view', { n: Object.keys(examSetBest()).length });
}

async function openSubject(id) {
    fireStart();
    stage('subject', { s: id });
    fe('subject_pick', { s: id });
    render(`<div class="egc-card egc-center">${esc(T.loading)}</div>`);
    try { subject = await loadSubject(id); }
    catch (e) {
        ae('tool_error', { error_type: 'subject_load_failed', s: id });
        toast(T.load_error, 'error'); viewPicker(); return;
    }
    st = blankState(id);
    // quick tab is pointless for grade-only subjects with tiny scales? No — keep for all.
    saveSession();
    viewSubject();
}

// ── view: subject (tabs) ──────────────────────────────────────────────────

function subjHeader() {
    return `
        <button type="button" class="egc-back" id="egcBack">${esc(T.back_to_subjects)}</button>
        <div class="egc-subj-head">
            <span class="egc-subj-ico" aria-hidden="true">${esc(subject.icon)}</span>
            <div class="egc-subj-head-t">${esc(subject.label)}</div>
            <div class="egc-duration">${esc(tr('duration_label', { d: fmtDuration(subject.durationMin) }))}</div>
        </div>
        <div class="egc-tabs" role="tablist">
            <button type="button" role="tab" class="egc-tab${st.tab === 'quick' ? ' on' : ''}" aria-selected="${st.tab === 'quick'}" data-tab="quick">${esc(T.tab_quick)}</button>
            <button type="button" role="tab" class="egc-tab${st.tab === 'detail' ? ' on' : ''}" aria-selected="${st.tab === 'detail'}" data-tab="detail">${esc(T.tab_detail)}</button>
        </div>`;
}

function viewSubject() {
    render(`
        <div class="egc-card">${subjHeader()}</div>
        <div id="egcTabBody"></div>
        ${disclaimerCard()}
    `);
    $('egcBack').addEventListener('click', () => { saveSession(); subject = null; viewPicker(); });
    root.querySelectorAll('.egc-tab').forEach(b => b.addEventListener('click', () => {
        if (st.tab === b.dataset.tab) return;
        st.tab = b.dataset.tab;
        fe('tab', { t: st.tab });
        saveSession();
        viewSubject();
    }));
    stage(st.tab === 'quick' ? 'quick' : 'detail', { s: st.subjectId });
    if (st.tab === 'quick') renderQuick($('egcTabBody'));
    else renderDetail($('egcTabBody'));
}

// ── quick converter ───────────────────────────────────────────────────────

function thresholdRows(test, primary) {
    const th = subject.thresholds || {};
    const rows = [];
    if (subject.kind === 'grade') {
        const passed = primary >= (th.attestatPrimary || 0);
        rows.push({ name: T.th_attestat, val: (toGrade(primary) || '2'), ok: passed });
        rows.push({ name: T.th_university, val: T.th_na, na: true });
    } else {
        if (th.attestat != null) rows.push({ name: T.th_attestat, val: th.attestat, ok: test >= th.attestat });
        if (th.university != null) rows.push({ name: T.th_university, val: th.university, ok: test >= th.university });
        if (th.minobr != null) rows.push({ name: T.th_minobr, val: th.minobr, ok: test >= th.minobr });
    }
    return `<div class="egc-th-list">` + rows.map(r => {
        const cls = r.na ? '' : (r.ok ? ' ok' : ' no');
        const ic = r.na ? '—' : (r.ok ? '✓' : '✗');
        const status = r.na ? '' : ' · ' + (r.ok ? T.th_ok : T.th_fail);
        return `<div class="egc-th${cls}"><span class="egc-th-ic" aria-hidden="true">${ic}</span>
            <span class="egc-th-name">${esc(r.name)}${esc(status)}</span>
            <span class="egc-th-val">${esc(String(r.val))}</span></div>`;
    }).join('') + `</div>`;
}

function scaleBar(test, primary) {
    if (subject.kind === 'grade') return '';
    const th = subject.thresholds || {};
    const marks = [];
    if (th.university != null) marks.push({ v: th.university, ok: test >= th.university });
    if (th.minobr != null && th.minobr !== th.university) marks.push({ v: th.minobr, ok: test >= th.minobr });
    const ticks = marks.map(m =>
        `<div class="egc-scalebar-th${m.ok ? ' ok' : ''}" style="left:${m.v}%"></div>
         <div class="egc-scalebar-th-l" style="left:${m.v}%">${m.v}</div>`).join('');
    return `<div class="egc-scalebar">
        <div class="egc-scalebar-tr"><div class="egc-scalebar-f" style="width:${test}%"></div></div>
        ${ticks}
    </div>`;
}

function quickResultBlock(primary) {
    const test = toTest(primary);
    const grade = toGrade(primary);
    let nextHint = '';
    if (subject.kind !== 'grade') {
        if (primary >= subject.maxPrimary || test >= 100) nextHint = T.quick_step_max;
        else {
            const nt = subject.scale[primary + 1];
            nextHint = tr('quick_step_hint', { test: nt, delta: nt - test });
        }
    }
    const big = subject.kind === 'grade' ? esc(grade || '—') : String(test);
    const lbl = subject.kind === 'grade' ? T.quick_grade_label : T.quick_test_label;
    return `
        <div class="egc-q-result">
            <div class="egc-q-test">${big}</div>
            <div class="egc-q-test-l">${esc(lbl)}</div>
            ${nextHint ? `<div class="egc-q-next">${esc(nextHint)}</div>` : ''}
        </div>`;
}

function fullScaleTable(cur) {
    const rows = [];
    for (let p = 0; p <= subject.maxPrimary; p++) {
        const val = subject.kind === 'grade' ? (toGradeFor(p)) : subject.scale[p];
        rows.push(`<tr${p === cur ? ' class="hit"' : ''}><td>${p}</td><td>${esc(String(val))}</td></tr>`);
    }
    const col2 = subject.kind === 'grade' ? T.quick_col_grade : T.quick_col_test;
    return `<details class="egc-details"><summary>${esc(T.quick_table_summary)}</summary>
        <div class="egc-table-wrap"><table class="egc-table">
        <thead><tr><th>${esc(T.quick_col_primary)}</th><th>${esc(col2)}</th></tr></thead>
        <tbody>${rows.join('')}</tbody></table></div></details>`;
}
function toGradeFor(p) {
    for (const g in subject.grades) { const [lo, hi] = subject.grades[g]; if (p >= lo && p <= hi) return g; }
    return '—';
}

function renderQuick(box) {
    const primary = Math.max(0, Math.min(st.quickPrimary || 0, subject.maxPrimary));
    st.quickPrimary = primary;
    const test = toTest(primary);
    box.innerHTML = `
        <div class="egc-card">
            <div class="egc-quick-grid">
                <div>
                    <label class="egc-q-label" for="egcQIn">${esc(T.quick_label)}</label>
                    <div class="egc-stepper">
                        <button type="button" class="egc-step-btn" id="egcQMinus" aria-label="−1" ${primary <= 0 ? 'disabled' : ''}>−</button>
                        <input id="egcQIn" class="egc-q-input" type="number" inputmode="numeric" min="0" max="${subject.maxPrimary}" value="${primary}">
                        <button type="button" class="egc-step-btn" id="egcQPlus" aria-label="+1" ${primary >= subject.maxPrimary ? 'disabled' : ''}>+</button>
                        <span class="egc-q-max">/ ${subject.maxPrimary}</span>
                    </div>
                    <input id="egcQSlider" class="egc-q-slider" type="range" min="0" max="${subject.maxPrimary}" value="${primary}" aria-label="${esc(T.quick_label)}">
                </div>
                <div id="egcQRes">${quickResultBlock(primary)}</div>
            </div>
            ${scaleBar(test == null ? 0 : test, primary)}
            <div class="egc-block-t">${esc(T.th_title)}</div>
            ${thresholdRows(test == null ? 0 : test, primary)}
        </div>
        ${goalCard(primary)}
        ${fullScaleTable(primary)}
        ${sourcesCard()}
    `;
    const clamp = (v) => Math.max(0, Math.min(parseInt(v, 10) || 0, subject.maxPrimary));
    const set = (v) => {
        st.quickPrimary = clamp(v);
        fe('quick_convert', { p: st.quickPrimary });
        saveSession();
        renderQuick(box);
    };
    $('egcQMinus').addEventListener('click', () => set(st.quickPrimary - 1));
    $('egcQPlus').addEventListener('click', () => set(st.quickPrimary + 1));
    $('egcQIn').addEventListener('change', e => set(e.target.value));
    // Slider: update the score/input/scale-fill LIVE during drag WITHOUT a full
    // re-render (which would recreate the <input type=range> under the cursor and
    // break the drag). Full re-render (thresholds, goal, table highlight) fires on
    // release via 'change'. Fixes: dragging the slider only moved one step.
    const slider = $('egcQSlider');
    slider.addEventListener('input', e => {
        const p = clamp(e.target.value);
        st.quickPrimary = p;
        const res = $('egcQRes'); if (res) res.innerHTML = quickResultBlock(p);
        const inp = $('egcQIn'); if (inp) inp.value = p;
        if (subject.kind !== 'grade') {
            const f = box.querySelector('.egc-scalebar-f');
            if (f) f.style.width = (toTest(p) || 0) + '%';
        }
    });
    slider.addEventListener('change', e => set(e.target.value));
    bindGoal(box, primary);
}

function sourcesCard() {
    if (!subject.sources || !subject.sources.length) return '';
    const items = subject.sources.map(s => `<p>· ${esc(s)}</p>`).join('');
    const notes = (subject.notes || []).map(n => `<p>${esc(n)}</p>`).join('');
    return `<details class="egc-details"><summary>${esc(T.sources_summary)}</summary>${notes}${items}</details>`;
}

// ── goal gap ──────────────────────────────────────────────────────────────

function goalCard(primary) {
    if (subject.kind === 'grade') return '';
    return `<div class="egc-card">
        <div class="egc-block-t">${esc(T.goal_title)}</div>
        <div class="egc-goal-row">
            <label class="egc-q-label" for="egcGoal">${esc(T.goal_label)}</label>
            <input id="egcGoal" class="egc-goal-input" type="number" inputmode="numeric" min="1" max="100" value="${st.goal || ''}" placeholder="80">
        </div>
        <div id="egcGoalOut"></div>
    </div>`;
}
function bindGoal(box, primary) {
    const inp = $('egcGoal');
    if (!inp) return;
    const out = $('egcGoalOut');
    const show = () => {
        const goal = parseInt(inp.value, 10);
        if (!goal || goal < 1 || goal > 100) { out.innerHTML = ''; return; }
        st.goal = goal; saveSession();
        const needP = primaryForTest(goal);
        if (needP == null) { out.innerHTML = `<div class="egc-goal-out">${esc(tr('goal_impossible', { goal }))}</div>`; return; }
        const cur = primary;
        if (cur >= needP) { out.innerHTML = `<div class="egc-goal-out good">${esc(tr('goal_reached', { goal }))}</div>`; return; }
        const need = needP - cur;
        // local slope: how many test points one primary is worth around here
        const span = subject.scale[Math.min(needP, subject.maxPrimary)] - subject.scale[cur];
        const avg = need > 0 ? Math.round((span / need) * 10) / 10 : 0;
        out.innerHTML = `<div class="egc-goal-out">${esc(tr('goal_need', { goal, need }))}</div>
            <div class="egc-goal-hint">${esc(tr('goal_need_hint', { avg }))}</div>`;
        fe('goal', { g: goal });
    };
    inp.addEventListener('input', show);
    show();
}

// ── detailed review ───────────────────────────────────────────────────────

function segButtons(t) {
    const v = st.marks[t.n]; // undefined | 0 | 1..max
    if (t.max === 1) {
        return `<div class="egc-seg" data-task="${t.n}">
            <button type="button" data-v="1" class="${v === 1 ? 'on right' : ''}" aria-pressed="${v === 1}">${esc(T.mark_right)}</button>
            <button type="button" data-v="0" class="${v === 0 ? 'on' : ''}" aria-pressed="${v === 0}">${esc(T.mark_wrong)}</button>
        </div>`;
    }
    let btns = '';
    for (let p = 0; p <= t.max; p++) {
        const on = v === p ? (p > 0 ? 'on right' : 'on') : '';
        btns += `<button type="button" data-v="${p}" class="${on}" aria-pressed="${v === p}">${p}</button>`;
    }
    return `<div class="egc-seg" data-task="${t.n}">${btns}</div>`;
}

// repaint a segmented control's buttons to reflect the selected value
function paintSeg(seg, value) {
    seg.querySelectorAll('button').forEach(b => {
        const v = parseInt(b.dataset.v, 10);
        const on = v === value;
        b.classList.toggle('on', on);
        b.classList.toggle('right', on && v > 0);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
}

function renderDetail(box) {
    const part1Rows = subject.part1.map(t => `
        <div class="egc-task">
            <span class="egc-task-n">${esc(tr('task_n', { n: t.n }))}</span>
            <span class="egc-task-topic">${esc(t.topic)}${t.partial ? ` <span class="egc-task-max">(${esc(t.partial)})</span>` : ''}</span>
            ${t.max > 1 ? `<span class="egc-task-max egc-task-pts">${esc(tr('points_of', { p: st.marks[t.n] ?? 0, max: t.max }))}</span>` : ''}
            ${segButtons(t)}
        </div>`).join('');

    const part2Html = subject.part2.length ? `
        <div class="egc-card">
            <div class="egc-part-t">${esc(T.part2_title)}</div>
            <div class="egc-part-hint">${esc(T.part2_hint)}</div>
            ${subject.part2.map(taskHtml).join('')}
        </div>` : '';

    box.innerHTML = `
        <div class="egc-card">
            <div class="egc-part-t">${esc(T.part1_title)}</div>
            <div class="egc-part-hint">${esc(T.part1_hint)}</div>
            <div class="egc-tasks">${part1Rows}</div>
        </div>
        ${part2Html}
        <div class="egc-live" id="egcLive"></div>
        <div class="egc-row egc-center-row">
            <button type="button" class="egc-btn ghost egc-btn-sm" id="egcReset">${esc(T.btn_reset)}</button>
        </div>
        ${sourcesCard()}
    `;

    // Part-1 clicks repaint only the clicked seg + live bar — no full re-render,
    // so open "how to check" hints and scroll position survive, and 34-task
    // subjects stay snappy on mobile. (Part 2 still full-re-renders for cascade.)
    box.querySelectorAll('.egc-tasks .egc-seg').forEach(seg => {
        seg.addEventListener('click', (ev) => {
            const b = ev.target.closest('button'); if (!b) return;
            const n = parseInt(seg.dataset.task, 10);
            const v = parseInt(b.dataset.v, 10);
            st.marks[n] = (st.marks[n] === v) ? undefined : v; // toggle off allowed
            saveSession();
            paintSeg(seg, st.marks[n]);
            const t = subject.part1.find(x => x.n === n);
            if (t && t.max > 1) {
                const lbl = seg.closest('.egc-task').querySelector('.egc-task-pts');
                if (lbl) lbl.textContent = tr('points_of', { p: st.marks[n] ?? 0, max: t.max });
            }
            updateLive();
        });
    });
    bindPart2(box);
    $('egcReset').addEventListener('click', () => {
        if (!confirm(T.confirm_reset)) return;
        const id = st.subjectId; const tab = st.tab;
        st = blankState(id); st.tab = tab;
        saveSession();
        renderDetail(box);
    });
    updateLive();
}

function taskHtml(t) {
    const gates = essayGates(t);
    let head = `<div class="egc-crit-task-t">${esc(tr('task_n', { n: t.n }))} — ${esc(t.label)} <span class="egc-task-max">(${esc(tr('crit_max', { max: t.max }))})</span></div>`;
    let extras = '';
    if (t.kind === 'essay') {
        const wr = t.wordRules || {};
        extras += `<div class="egc-words-row">
            <label class="egc-words-l" for="egcWords${t.n}">${esc(T.essay_words_label)}</label>
            <input id="egcWords${t.n}" class="egc-goal-input" type="number" inputmode="numeric" min="0" max="9999" value="${st.essayWords ?? ''}" placeholder="250">
            <span class="egc-words-hint">${esc(T.essay_words_hint)}${wr.note ? ' ' + esc(wr.note) : ''}</span>
        </div>`;
        for (const f of (t.zeroFlags || [])) {
            extras += `<label class="egc-checkline"><input type="checkbox" data-flag="${esc(f.id)}" ${st.flags[f.id] ? 'checked' : ''}>
                <span>${esc(T.essay_no_text_label)}</span></label>`;
        }
        if (gates.zeroAll && st.essayWords != null && (t.wordRules && st.essayWords < t.wordRules.minWords)) {
            extras += `<div class="egc-warn">${esc(tr('essay_zero_warn', { n: t.wordRules.minWords }))}</div>`;
        } else if (gates.zeroAll) {
            extras += `<div class="egc-warn">${esc(T.essay_no_text_warn)}</div>`;
        }
    }
    const crits = t.criteria.map(c => {
        const disabled = gates.zeroAll || gates.cascadeZero.has(c.code);
        const v = st.critMarks[t.n + ':' + c.code];
        let btns = '';
        for (let p = 0; p <= c.max; p++) {
            const on = (!disabled && v === p) ? (p > 0 ? 'on right' : 'on') : '';
            btns += `<button type="button" data-v="${p}" class="${on}" ${disabled ? 'disabled' : ''} aria-pressed="${v === p}">${p}</button>`;
        }
        const offNote = (disabled && gates.cascadeZero.has(c.code) && !gates.zeroAll)
            ? `<div class="egc-crit-off-note">${esc(tr('crit_cascade_off', { src: cascadeSourceOf(t, c.code) }))}</div>` : '';
        return `<div class="egc-crit${disabled ? ' egc-crit-off' : ''}">
            <div class="egc-crit-head">
                ${c.code ? `<span class="egc-crit-code">${esc(c.code)}</span>` : ''}
                <span class="egc-crit-title">${esc(c.title)}</span>
                <div class="egc-seg" data-crit="${t.n}:${esc(c.code)}">${btns}</div>
            </div>
            <div class="egc-crit-desc">${esc(c.desc)}</div>
            ${offNote}
            ${c.hint ? `<details class="egc-crit-hint"><summary>${esc(T.crit_hint_toggle)}</summary><p>${esc(c.hint)}</p></details>` : ''}
        </div>`;
    }).join('');
    return `<div class="egc-crit-task">${head}${extras}${crits}</div>`;
}

function bindPart2(box) {
    box.querySelectorAll('.egc-seg[data-crit]').forEach(seg => {
        seg.addEventListener('click', (ev) => {
            const b = ev.target.closest('button'); if (!b || b.disabled) return;
            const key = seg.dataset.crit;
            const v = parseInt(b.dataset.v, 10);
            st.critMarks[key] = (st.critMarks[key] === v) ? undefined : v;
            saveSession();
            // Full re-render only when this criterion can gate others (a cascade
            // source like К1). Otherwise repaint locally so open hints survive.
            const taskN = parseInt(key.split(':')[0], 10);
            const code = key.split(':')[1];
            const t = subject.part2.find(x => x.n === taskN);
            const isCascadeSource = t && t.cascade && Object.prototype.hasOwnProperty.call(t.cascade, code);
            if (isCascadeSource) { renderDetail(box); return; }
            paintSeg(seg, st.critMarks[key]);
            updateLive();
        });
    });
    subject.part2.forEach(t => {
        if (t.kind !== 'essay') return;
        const w = $('egcWords' + t.n);
        if (w) w.addEventListener('change', () => {
            const v = parseInt(w.value, 10);
            st.essayWords = isNaN(v) ? null : v;
            fe('essay_words', { w: st.essayWords });
            saveSession();
            renderDetail(box.closest('#egcTabBody') || box);
        });
        box.querySelectorAll('input[data-flag]').forEach(chk => {
            chk.addEventListener('change', () => {
                st.flags[chk.dataset.flag] = chk.checked;
                saveSession();
                renderDetail(box);
            });
        });
    });
}

function updateLive() {
    const live = $('egcLive');
    if (!live) return;
    const primary = detailPrimary();
    const test = toTest(primary);
    const grade = toGrade(primary);
    const second = subject.kind === 'grade'
        ? `<div class="egc-live-item"><span class="egc-live-l">${esc(T.live_grade)}</span><span class="egc-live-v">${esc(grade || '—')}</span></div>`
        : `<div class="egc-live-item"><span class="egc-live-l">${esc(T.live_test)}</span><span class="egc-live-v">${test}</span></div>`;
    live.innerHTML = `
        <div class="egc-live-item"><span class="egc-live-l">${esc(T.live_primary)}</span><span class="egc-live-v">${primary} / ${subject.maxPrimary}</span></div>
        ${second}
        <span class="egc-live-spacer"></span>
        <button type="button" class="egc-btn" id="egcShowRes">${esc(T.btn_result)}</button>`;
    $('egcShowRes').addEventListener('click', () => { fe('result_open', { p: primary }); viewResult({ scroll: true }); });
}

// ── result view ───────────────────────────────────────────────────────────

function lossRows() {
    const rows = [];
    for (const t of subject.part1) {
        const v = st.marks[t.n];
        const earned = (typeof v === 'number' && v > 0) ? Math.min(v, t.max) : 0;
        const lost = t.max - earned;
        if (lost > 0) rows.push({ label: tr('task_n', { n: t.n }) + ' · ' + t.topic, lost, max: t.max, part: 1 });
    }
    for (const t of subject.part2) {
        for (const c of t.criteria) {
            const earned = critEffective(t, c);
            const lost = c.max - earned;
            if (lost > 0) rows.push({ label: (subject.part2.length > 1 ? tr('task_n', { n: t.n }) + ' · ' : '') + (c.code ? c.code + ' ' : '') + c.title, lost, max: c.max, part: 2 });
        }
    }
    rows.sort((a, b) => b.lost - a.lost);
    return rows;
}

function viewResult(opts) {
    const primary = detailPrimary();
    if (!completedFired) {
        completedFired = true;
        stage('result', { s: st.subjectId, p: primary }); // funnel marker
        ae('tool_complete', { s: st.subjectId, p: primary });
    } else {
        currentStage = 'result';
    }
    const test = toTest(primary);
    const grade = toGrade(primary);
    const losses = lossRows().slice(0, 10);
    const big = subject.kind === 'grade' ? esc(grade || '—') : String(test);
    const pct = Math.round((primary / subject.maxPrimary) * 100);

    const lossesHtml = losses.length
        ? losses.map(r => {
            const w = Math.round((r.lost / r.max) * 100);
            return `<div class="egc-loss">
                <span class="egc-loss-l">${esc(r.label)}</span>
                <div class="egc-loss-tr"><div class="egc-loss-f" style="width:${w}%"></div></div>
                <span class="egc-loss-v">${esc(tr('loss_row', { lost: r.lost, max: r.max }))}</span>
            </div>`;
        }).join('') + `<div class="egc-goal-hint">${esc(T.loss_hint)}</div>`
        : `<div class="egc-goal-out good">${esc(T.loss_none)}</div>`;

    render(`
        <div class="egc-card egc-center" id="egcResCard">
            <div class="egc-res-eyebrow">${esc(tr('res_eyebrow', { subject: subject.label }))}</div>
            <div class="egc-res-test">${big}</div>
            <div class="egc-res-sub">${esc(subject.kind === 'grade' ? T.res_grade_label : T.res_test_label)} ·
                ${esc(tr('res_primary_label', { p: primary, max: subject.maxPrimary }))} · ${esc(tr('res_percent', { percent: pct }))}</div>
            ${scaleBar(test == null ? 0 : test, primary)}
            ${thresholdRows(test == null ? 0 : test, primary)}
        </div>
        ${goalCard(primary)}
        <div class="egc-card">
            <div class="egc-block-t">${esc(T.loss_title)}</div>
            ${lossesHtml}
        </div>
        <div class="egc-actions">
            <button type="button" class="egc-btn" id="egcSave">${esc(T.btn_save)}</button>
            <button type="button" class="egc-btn ghost" id="egcPdf">${esc(T.btn_pdf)}</button>
            <button type="button" class="egc-btn ghost" id="egcBackDetail">${esc(T.tab_detail)}</button>
            <button type="button" class="egc-btn ghost" id="egcRetake">${esc(T.btn_retake)}</button>
        </div>
        ${historyCard()}
        ${disclaimerCard()}
    `);
    if (opts && opts.scroll) scrollTop();
    bindGoal(root, primary);
    $('egcSave').addEventListener('click', () => {
        pushHistory({ d: Date.now(), s: st.subjectId, p: primary, t: test, g: grade });
        fe('save_history', { p: primary });
        toast(T.saved_ok, 'success');
        announce(T.saved_ok);
        viewResult(); // re-render for the history card; no scroll (user is mid-page)
    });
    $('egcPdf').addEventListener('click', () => {
        fe('pdf');
        if (typeof window.__generatePDF !== 'function') { toast(T.load_error, 'error'); return; }
        window.__generatePDF({
            element: $('egcResCard'),
            filename: 'ege-' + st.subjectId + '-report.pdf',
            title: T.pdf_title,
        });
    });
    $('egcBackDetail').addEventListener('click', () => { st.tab = 'detail'; viewSubject(); });
    $('egcRetake').addEventListener('click', () => {
        clearSession(); st = null; subject = null; completedFired = false;
        viewPicker();
    });
    bindHistoryCard();
}

// ── history ───────────────────────────────────────────────────────────────

function historyCard() {
    const h = history();
    if (!h.length) return '';
    const bySubj = (id) => (manifest.subjects.find(s => s.id === id) || { label: id }).label;
    const rows = h.slice().reverse().slice(0, 12).map((e, i, arr) => {
        const prev = arr[i + 1];
        const delta = (prev && prev.s === e.s && e.t != null && prev.t != null) ? (e.t - prev.t) : null;
        const deltaStr = delta == null ? '' : (delta >= 0 ? ' (+' + delta + ')' : ' (' + delta + ')');
        const score = e.t != null ? e.t + deltaStr : (e.g || '—');
        const d = new Date(e.d);
        const ds = d.toLocaleDateString(cfg.locale || 'ru', { day: 'numeric', month: 'short' });
        return `<tr><td>${esc(ds)}</td><td>${esc(bySubj(e.s))}</td><td>${e.p}</td><td>${esc(String(score))}</td></tr>`;
    }).join('');
    return `<details class="egc-details"><summary>${esc(T.hist_title)}</summary>
        <div class="egc-table-wrap"><table class="egc-table">
        <thead><tr><th>${esc(T.hist_col_date)}</th><th>${esc(T.hist_col_subject)}</th><th>${esc(T.hist_col_primary)}</th><th>${esc(T.hist_col_test)}</th></tr></thead>
        <tbody>${rows}</tbody></table></div>
        <div class="egc-hist-note">${esc(T.hist_note)}</div>
        <div class="egc-row"><button type="button" class="egc-btn ghost egc-btn-sm" id="egcHistClear">${esc(T.hist_clear)}</button></div>
    </details>`;
}
function bindHistoryCard() {
    const b = $('egcHistClear');
    if (b) b.addEventListener('click', () => {
        if (!confirm(T.hist_confirm_clear)) return;
        try { localStorage.removeItem(LS_HISTORY); } catch (e) {}
        toast(T.hist_clear, 'info');
        // re-render current view without the card
        if (subject && st) viewSubject(); else viewPicker();
    });
}

// ── boot ──────────────────────────────────────────────────────────────────

// Fire tool_abandon once if the user started (picked a subject) but left before
// seeing a result. Matches the funnel other test tools emit (english-level-test).
function fireAbandon() {
    if (!firedStart || completedFired || abandonFired) return;
    abandonFired = true;
    ae('tool_abandon', { stage: currentStage });
}
try {
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') fireAbandon(); });
    window.addEventListener('pagehide', fireAbandon);
} catch (e) {}

(async function boot() {
    try {
        manifest = await loadManifest();
    } catch (e) {
        root.removeAttribute('data-boot');
        render(`<div class="egc-card egc-center">${esc(T.load_error)}</div>`);
        claimEarly(false);
        ae('tool_error', { error_type: 'manifest_load_failed' });
        try { window.__handleError && window.__handleError(e, 'ege-manifest'); } catch (e2) {}
        return;
    }
    viewPicker();
    claimEarly(!!startNode && !savedSession());
})();
